/**
 * AiCreditCosts — Super Admin Settings Page
 *
 * Control panel for configuring AI credit costs per action.
 * Each action type deducts a configurable number of credits from tenant wallets.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap,
  MessageSquare,
  FileText,
  BarChart2,
  Check,
  X,
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  Keyboard,
} from 'lucide-react';
import { cn, Button, Switch } from '@respectly/ui';
import { useAuth } from '@respectly/utils/auth';

const API_URL = import.meta.env.VITE_API_URL ?? '';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AiActionCost {
  id: string;
  action: string;
  label: string;
  description: string | null;
  credits: number;
  category: 'messaging' | 'documents' | 'analysis';
  isActive: boolean;
  updatedAt: string;
}

// ─── Toast ────────────────────────────────────────────────────────────────────

interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}

let toastCounter = 0;

function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((type: Toast['type'], message: string) => {
    const id = ++toastCounter;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  }, []);

  return { toasts, addToast };
}

// ─── API helpers ─────────────────────────────────────────────────────────────
// Built inside the component via useApiFetch() so the MSAL token is available.

type ApiFetch = (path: string, options?: RequestInit) => Promise<Response>;

function useApiFetch(): ApiFetch {
  const { getAccessToken } = useAuth();

  return useCallback(async (path: string, options?: RequestInit) => {
    const token = await getAccessToken();
    return fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options?.headers ?? {}),
      },
    });
  }, [getAccessToken]);
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="flex items-center gap-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-5 py-4 animate-pulse">
      <div className="h-14 w-14 rounded-lg bg-slate-100 dark:bg-slate-800 shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3.5 w-40 rounded bg-slate-100 dark:bg-slate-800" />
        <div className="h-3 w-64 rounded bg-slate-100 dark:bg-slate-800" />
      </div>
      <div className="h-5 w-9 rounded-full bg-slate-100 dark:bg-slate-800" />
    </div>
  );
}

function SkeletonSection() {
  return (
    <div className="space-y-2">
      <div className="mb-4 h-4 w-28 rounded bg-slate-100 dark:bg-slate-800 animate-pulse" />
      {[1, 2, 3].map((i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

// ─── Action Card ──────────────────────────────────────────────────────────────

function ActionCard({
  cost,
  apiFetch,
  onUpdate,
  addToast,
}: {
  cost: AiActionCost;
  apiFetch: ApiFetch;
  onUpdate: (updated: AiActionCost) => void;
  addToast: (type: Toast['type'], msg: string) => void;
}) {
  const [draftCredits, setDraftCredits] = useState(cost.credits);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraftCredits(cost.credits);
    setIsDirty(false);
  }, [cost.credits]);

  const handleCreditsChange = (val: number) => {
    const clamped = Math.max(1, Math.min(100, val));
    setDraftCredits(clamped);
    setIsDirty(clamped !== cost.credits);
  };

  const handleSaveCredits = async () => {
    if (!isDirty || saving) return;
    const prev = cost.credits;
    setSaving(true);
    onUpdate({ ...cost, credits: draftCredits });
    try {
      const res = await apiFetch(`/api/super-admin/ai-credit-costs/${cost.action}`, {
        method: 'PATCH',
        body: JSON.stringify({ credits: draftCredits }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Update failed');
      onUpdate(json.data as AiActionCost);
      addToast('success', `${cost.label} updated to ${draftCredits} credit${draftCredits === 1 ? '' : 's'}`);
      setIsDirty(false);
    } catch (err) {
      onUpdate({ ...cost, credits: prev });
      setDraftCredits(prev);
      setIsDirty(false);
      addToast('error', err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (active: boolean) => {
    if (toggling) return;
    const prev = cost.isActive;
    setToggling(true);
    onUpdate({ ...cost, isActive: active });
    try {
      const res = await apiFetch(`/api/super-admin/ai-credit-costs/${cost.action}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: active }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message ?? 'Toggle failed');
      onUpdate(json.data as AiActionCost);
      addToast('success', `${cost.label} ${active ? 'enabled' : 'disabled'}`);
    } catch (err) {
      onUpdate({ ...cost, isActive: prev });
      addToast('error', err instanceof Error ? err.message : 'Toggle failed');
    } finally {
      setToggling(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveCredits();
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      setDraftCredits(cost.credits);
      setIsDirty(false);
      inputRef.current?.blur();
    }
  };

  return (
    <motion.div
      layout
      className={cn(
        'group relative flex items-center gap-4 rounded-lg border px-5 py-4 transition-all duration-200',
        cost.isActive
          ? 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700/60 hover:border-slate-300 dark:hover:border-slate-600 shadow-sm'
          : 'bg-slate-50 dark:bg-slate-900/40 border-slate-100 dark:border-slate-800/50 opacity-60'
      )}
    >
      {/* Credit cost badge — editable */}
      <div className="shrink-0 text-center">
        <div
          className={cn(
            'relative flex h-14 w-14 flex-col items-center justify-center rounded-lg border font-mono transition-colors',
            isDirty
              ? 'border-amber-400/60 dark:border-amber-500/50 bg-amber-100 dark:bg-amber-950/40'
              : cost.isActive
              ? 'border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-950/20'
              : 'border-slate-200 dark:border-slate-700/40 bg-slate-100/60 dark:bg-slate-800/30'
          )}
        >
          <input
            ref={inputRef}
            type="number"
            min={1}
            max={100}
            value={draftCredits}
            disabled={!cost.isActive || saving}
            onChange={(e) => handleCreditsChange(parseInt(e.target.value) || 1)}
            onKeyDown={handleKeyDown}
            className={cn(
              'w-full bg-transparent text-center text-2xl font-bold leading-none tracking-tighter outline-none transition-colors disabled:cursor-not-allowed',
              cost.isActive
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-slate-400 dark:text-slate-600',
              '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
            )}
          />
          <span
            className={cn(
              'mt-0.5 text-[9px] uppercase tracking-widest transition-colors',
              cost.isActive
                ? 'text-amber-500/80 dark:text-amber-600'
                : 'text-slate-400 dark:text-slate-600'
            )}
          >
            {draftCredits === 1 ? 'credit' : 'credits'}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={cn(
              'text-sm font-semibold tracking-tight transition-colors',
              cost.isActive
                ? 'text-slate-900 dark:text-slate-100'
                : 'text-slate-500 dark:text-slate-500'
            )}
          >
            {cost.label}
          </span>
          <span className="rounded-md bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] text-slate-500 dark:text-slate-400">
            {cost.action}
          </span>
        </div>
        {cost.description && (
          <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-500 line-clamp-1">
            {cost.description}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-3">
        <AnimatePresence>
          {isDirty && (
            <motion.div
              initial={{ opacity: 0, scale: 0.85, x: 8 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.85, x: 8 }}
              className="flex items-center gap-1.5"
            >
              <button
                onClick={() => {
                  setDraftCredits(cost.credits);
                  setIsDirty(false);
                }}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 transition hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-700 dark:hover:text-slate-300"
                title="Discard"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={handleSaveCredits}
                disabled={saving}
                className="flex h-7 items-center gap-1.5 rounded-md border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/50 px-2.5 text-xs font-medium text-amber-700 dark:text-amber-400 transition hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-50"
              >
                {saving ? (
                  <RefreshCw className="h-3 w-3 animate-spin" />
                ) : (
                  <Check className="h-3 w-3" />
                )}
                Save
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <Switch
          checked={cost.isActive}
          onCheckedChange={handleToggle}
          disabled={toggling}
          aria-label={`Toggle ${cost.label}`}
        />
      </div>
    </motion.div>
  );
}

// ─── Category Section ─────────────────────────────────────────────────────────

const CATEGORY_META: Record<
  AiActionCost['category'],
  {
    label: string;
    icon: React.ReactNode;
    textClass: string;
    borderClass: string;
    badgeClass: string;
  }
> = {
  messaging: {
    label: 'Messaging',
    icon: <MessageSquare className="h-3.5 w-3.5" />,
    textClass: 'text-sky-600 dark:text-sky-400',
    borderClass: 'border-sky-400 dark:border-sky-700/60',
    badgeClass: 'bg-sky-50 dark:bg-sky-950/30 text-sky-600 dark:text-sky-400',
  },
  documents: {
    label: 'Documents & Forms',
    icon: <FileText className="h-3.5 w-3.5" />,
    textClass: 'text-violet-600 dark:text-violet-400',
    borderClass: 'border-violet-400 dark:border-violet-700/60',
    badgeClass: 'bg-violet-50 dark:bg-violet-950/30 text-violet-600 dark:text-violet-400',
  },
  analysis: {
    label: 'Analysis',
    icon: <BarChart2 className="h-3.5 w-3.5" />,
    textClass: 'text-emerald-600 dark:text-emerald-400',
    borderClass: 'border-emerald-400 dark:border-emerald-700/60',
    badgeClass: 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400',
  },
};

const CATEGORY_ORDER: AiActionCost['category'][] = ['messaging', 'documents', 'analysis'];

function CategorySection({
  category,
  costs,
  apiFetch,
  onUpdate,
  addToast,
}: {
  category: AiActionCost['category'];
  costs: AiActionCost[];
  apiFetch: ApiFetch;
  onUpdate: (updated: AiActionCost) => void;
  addToast: (type: Toast['type'], msg: string) => void;
}) {
  const meta = CATEGORY_META[category];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Section header */}
      <div className={cn('mb-3 flex items-center gap-2.5 border-l-2 pl-3', meta.borderClass)}>
        <span className={meta.textClass}>{meta.icon}</span>
        <span className={cn('text-xs font-semibold uppercase tracking-[0.1em]', meta.textClass)}>
          {meta.label}
        </span>
        <span
          className={cn(
            'ml-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
            meta.badgeClass
          )}
        >
          {costs.length} action{costs.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Cards */}
      <div className="space-y-2">
        {costs.map((cost, i) => (
          <motion.div
            key={cost.id}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.04, duration: 0.22 }}
          >
            <ActionCard cost={cost} apiFetch={apiFetch} onUpdate={onUpdate} addToast={addToast} />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

// ─── Toast Renderer ───────────────────────────────────────────────────────────

function ToastStack({ toasts }: { toasts: Toast[] }) {
  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
      <AnimatePresence>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: 16, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={cn(
              'flex items-center gap-2.5 rounded-lg border px-4 py-3 text-sm font-medium shadow-lg',
              t.type === 'success'
                ? 'border-emerald-200 dark:border-emerald-700/50 bg-white dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300'
                : 'border-red-200 dark:border-red-700/50 bg-white dark:bg-red-950 text-red-700 dark:text-red-300'
            )}
          >
            {t.type === 'success' ? (
              <Check className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-red-500 dark:text-red-400" />
            )}
            {t.message}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ─── Shared Data Hook ─────────────────────────────────────────────────────────

function useAiCreditCostsData() {
  const apiFetch = useApiFetch();
  const [costs, setCosts] = useState<AiActionCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toasts, addToast } = useToasts();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/super-admin/ai-credit-costs');
      if (!res.ok) throw new Error('Failed to fetch AI action costs');
      const json = await res.json();
      setCosts((json.data ?? []) as AiActionCost[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpdate = useCallback((updated: AiActionCost) => {
    setCosts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  }, []);

  const grouped = CATEGORY_ORDER.reduce<Record<AiActionCost['category'], AiActionCost[]>>(
    (acc, cat) => {
      acc[cat] = costs.filter((c) => c.category === cat);
      return acc;
    },
    { messaging: [], documents: [], analysis: [] }
  );

  const totalActive = costs.filter((c) => c.isActive).length;
  const avgCost =
    costs.length > 0
      ? (costs.reduce((sum, c) => sum + c.credits, 0) / costs.length).toFixed(1)
      : null;

  return { apiFetch, costs, loading, error, load, handleUpdate, grouped, totalActive, avgCost, toasts, addToast };
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function AiCreditCosts() {
  const { apiFetch, costs, loading, error, load, handleUpdate, grouped, totalActive, avgCost, toasts, addToast } = useAiCreditCostsData();

  return (
    <div className="flex h-full flex-col">
      {/* ── Page header ── */}
      <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between p-4 lg:p-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-sm">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                AI Credit Costs
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Configure how many credits each AI action costs per invocation
              </p>
            </div>
          </div>

          {/* Stats chips — shown once data is loaded */}
          {!loading && costs.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-2 text-center">
                <div className="font-mono text-base font-bold text-amber-600 dark:text-amber-400 leading-none">
                  {avgCost}
                </div>
                <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-500">
                  avg
                </div>
              </div>
              <div className="rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-2 text-center">
                <div className="font-mono text-base font-bold text-slate-700 dark:text-slate-200 leading-none">
                  {totalActive}
                </div>
                <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-500">
                  active
                </div>
              </div>
              <div className="rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-2 text-center">
                <div className="font-mono text-base font-bold text-slate-700 dark:text-slate-200 leading-none">
                  {costs.length}
                </div>
                <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-500">
                  total
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Hint bar */}
        <div className="flex items-center gap-5 px-4 lg:px-6 pb-3 border-t border-slate-100 dark:border-slate-800 pt-3">
          <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
            <Keyboard className="h-3.5 w-3.5" />
            <span>Click a number to edit</span>
            <ChevronRight className="h-3 w-3 opacity-60" />
            <kbd className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] text-slate-500 dark:text-slate-400">Enter</kbd>
            <span>to save</span>
            <span className="mx-1 opacity-40">·</span>
            <kbd className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] text-slate-500 dark:text-slate-400">Esc</kbd>
            <span>to cancel</span>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-950 p-4 lg:p-6">
        {loading ? (
          <div className="space-y-8">
            {CATEGORY_ORDER.map((cat) => (
              <SkeletonSection key={cat} />
            ))}
          </div>
        ) : error ? (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-800/50 dark:bg-red-950/20 px-4 py-3.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-900 dark:text-red-200">
                Failed to load AI action costs
              </p>
              <p className="mt-0.5 text-xs text-red-700/80 dark:text-red-400/80">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={load}>
              <RefreshCw className="mr-1.5 h-3 w-3" />
              Retry
            </Button>
          </div>
        ) : costs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
              <Zap className="h-8 w-8 text-slate-400 dark:text-slate-500" />
            </div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-1">
              No AI actions configured
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs mb-5">
              AI action cost records will appear here once the database has been seeded.
            </p>
            <Button variant="outline" size="sm" onClick={load}>
              <RefreshCw className="mr-1.5 h-3 w-3" />
              Refresh
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {CATEGORY_ORDER.map((cat) => {
              const items = grouped[cat];
              if (items.length === 0) return null;
              return (
                <CategorySection
                  key={cat}
                  category={cat}
                  costs={items}
                  apiFetch={apiFetch}
                  onUpdate={handleUpdate}
                  addToast={addToast}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* ── Toasts ── */}
      <ToastStack toasts={toasts} />
    </div>
  );
}

// ─── Tab Version (embedded in PlanSetupPage) ──────────────────────────────────

export function AiCreditCostsTab() {
  const { apiFetch, costs, loading, error, load, handleUpdate, grouped, totalActive, avgCost, toasts, addToast } = useAiCreditCostsData();

  return (
    <>
      {/* Toolbar: keyboard hints + live stats */}
      <div className="flex items-center justify-between px-4 lg:px-6 py-3 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
          <Keyboard className="h-3.5 w-3.5" />
          <span>Click a number to edit</span>
          <ChevronRight className="h-3 w-3 opacity-60" />
          <kbd className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] text-slate-500 dark:text-slate-400">Enter</kbd>
          <span>to save</span>
          <span className="mx-1 opacity-40">·</span>
          <kbd className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] text-slate-500 dark:text-slate-400">Esc</kbd>
          <span>to cancel</span>
        </div>
        {!loading && costs.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-1.5 text-center">
              <div className="font-mono text-sm font-bold text-amber-600 dark:text-amber-400 leading-none">{avgCost}</div>
              <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-500">avg</div>
            </div>
            <div className="rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-1.5 text-center">
              <div className="font-mono text-sm font-bold text-slate-700 dark:text-slate-200 leading-none">{totalActive}</div>
              <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-500">active</div>
            </div>
            <div className="rounded-lg bg-slate-100 dark:bg-slate-800 px-3 py-1.5 text-center">
              <div className="font-mono text-sm font-bold text-slate-700 dark:text-slate-200 leading-none">{costs.length}</div>
              <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-500">total</div>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 lg:p-6 bg-slate-50 dark:bg-slate-950">
        {loading ? (
          <div className="space-y-8">
            {CATEGORY_ORDER.map((cat) => (
              <SkeletonSection key={cat} />
            ))}
          </div>
        ) : error ? (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-800/50 dark:bg-red-950/20 px-4 py-3.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-900 dark:text-red-200">Failed to load AI action costs</p>
              <p className="mt-0.5 text-xs text-red-700/80 dark:text-red-400/80">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={load}>
              <RefreshCw className="mr-1.5 h-3 w-3" />
              Retry
            </Button>
          </div>
        ) : costs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
              <Zap className="h-8 w-8 text-slate-400 dark:text-slate-500" />
            </div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-1">No AI actions configured</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs mb-5">
              AI action cost records will appear here once the database has been seeded.
            </p>
            <Button variant="outline" size="sm" onClick={load}>
              <RefreshCw className="mr-1.5 h-3 w-3" />
              Refresh
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {CATEGORY_ORDER.map((cat) => {
              const items = grouped[cat];
              if (items.length === 0) return null;
              return (
                <CategorySection
                  key={cat}
                  category={cat}
                  costs={items}
                  apiFetch={apiFetch}
                  onUpdate={handleUpdate}
                  addToast={addToast}
                />
              );
            })}
          </div>
        )}
      </div>

      <ToastStack toasts={toasts} />
    </>
  );
}
